/**
 * Stone Investment Area Map
 * 
 * Map section for area pages with Stone Investment's luxury aesthetic:
 * - Light-themed interactive Leaflet map
 * - Coordinates display
 * - Directions link
 */

'use client';

import { useEffect, useRef } from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import type { Map as LeafletMap } from 'leaflet';

export interface StoneAreaMapProps {
  area: {
    name: string;
    latitude: number;
    longitude: number;
    coordinates?: string;
  };
  messages?: Record<string, string>;
  className?: string;
}

export function StoneAreaMap({
  area,
  messages = {},
  className = '',
}: StoneAreaMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${area.latitude},${area.longitude}`;

  const formatCoordinate = (value: number, isLatitude: boolean): string => {
    const direction = isLatitude
      ? (value >= 0 ? 'N' : 'S')
      : (value >= 0 ? 'E' : 'W');
    const absValue = Math.abs(value);
    const degrees = Math.floor(absValue);
    const minutes = Math.floor((absValue - degrees) * 60);
    const seconds = Math.round(((absValue - degrees) * 60 - minutes) * 60);
    return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let cancelled = false;

    (async () => {
      const leaflet = await import('leaflet');
      // @ts-expect-error -- CSS import handled by bundler
      await import('leaflet/dist/leaflet.css');

      if (cancelled || !containerRef.current) return;

      const map = leaflet.map(containerRef.current, {
        center: [area.latitude, area.longitude],
        zoom: 14,
        zoomControl: false,
        scrollWheelZoom: false,
        attributionControl: false,
      });

      leaflet.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        subdomains: 'abcd',
      }).addTo(map);

      const markerIcon = leaflet.divIcon({
        className: 'stone-map-marker',
        html: `<div style="
          width: 32px;
          height: 32px;
          background: #292524;
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>`,
        iconSize: [32, 32] as [number, number],
        iconAnchor: [16, 16] as [number, number],
      });

      leaflet.marker([area.latitude, area.longitude], { icon: markerIcon }).addTo(map);

      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [area.latitude, area.longitude]);

  return (
    <div className={`bg-white h-full flex flex-col ${className}`}>
      {/* Map Container */}
      <div className="relative flex-1 min-h-[300px]">
        <div ref={containerRef} className="absolute inset-0" />

        {/* Open in Maps Button */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 right-4 z-[1000] flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-stone-300 text-stone-700 hover:border-stone-400 hover:text-stone-900 transition-colors text-sm font-sans"
        >
          <span>{messages.viewOnMap || 'View on Map'}</span>
          <ExternalLink size={14} strokeWidth={1.5} />
        </a>
      </div>
      
      {/* Coordinates Footer */}
      <div className="p-6 bg-stone-50 border-t border-stone-200">
        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
          {messages.coordinates || 'Coordinates'}
        </p>
        <div className="flex flex-col gap-1">
          <p className="font-serif text-lg text-stone-900 italic">
            {formatCoordinate(area.latitude, true)}
          </p>
          <p className="font-serif text-lg text-stone-700 italic">
            {formatCoordinate(area.longitude, false)}
          </p>
        </div>
        
        {/* Directions Link */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 text-sm font-sans text-stone-600 hover:text-stone-900 transition-colors"
        >
          <MapPin size={14} strokeWidth={1.5} />
          <span>{messages.getDirections || 'Get Directions'}</span>
        </a>
      </div>
    </div>
  );
}

export default StoneAreaMap;
